import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface OverlayLoadingState {
  show: boolean;
  message?: string;
}

/**
 * Service for managing loading states across the application.
 * Supports two types of loading indicators:
 * 1. Modal Overlay - Full-screen blocking overlay with spinner
 * 2. Button Spinner - Inline button loading state
 */
@Injectable({
  providedIn: 'root'
})
export class LoadingService {
  // Modal Overlay State
  private overlayLoadingMap = new Map<string, string>();
  private overlayLoadingSubject = new BehaviorSubject<OverlayLoadingState>({ show: false });
  public overlayLoading$: Observable<OverlayLoadingState> = this.overlayLoadingSubject.asObservable();

  // Button Spinner State
  private buttonLoadingMap = new Map<string, BehaviorSubject<boolean>>();

  constructor() {}

  // ========== Modal Overlay Methods ==========

  /**
   * Show a full-screen modal overlay with loading spinner
   * @param key Unique identifier for this loading operation (default: 'global')
   * @param message Optional message to display
   */
  showOverlay(key: string = 'global', message?: string): void {
    this.overlayLoadingMap.set(key, message || '');
    this.updateOverlayState();
  }

  /**
   * Hide the modal overlay for a specific key
   * @param key Unique identifier for the loading operation to hide
   */
  hideOverlay(key: string = 'global'): void {
    this.overlayLoadingMap.delete(key);
    this.updateOverlayState();
  }

  /**
   * Hide all modal overlays
   */
  hideAllOverlays(): void {
    this.overlayLoadingMap.clear();
    this.updateOverlayState();
  }

  private updateOverlayState(): void {
    if (this.overlayLoadingMap.size > 0) {
      // Get the most recent message (last one added)
      const messages = Array.from(this.overlayLoadingMap.values());
      const message = messages[messages.length - 1];
      this.overlayLoadingSubject.next({ show: true, message });
    } else {
      this.overlayLoadingSubject.next({ show: false });
    }
  }

  // ========== Button Spinner Methods ==========

  /**
   * Show loading spinner on a specific button
   * @param buttonKey Unique identifier for the button
   */
  showButtonSpinner(buttonKey: string): void {
    if (!this.buttonLoadingMap.has(buttonKey)) {
      this.buttonLoadingMap.set(buttonKey, new BehaviorSubject<boolean>(false));
    }
    this.buttonLoadingMap.get(buttonKey)!.next(true);
  }

  /**
   * Hide loading spinner on a specific button
   * @param buttonKey Unique identifier for the button
   */
  hideButtonSpinner(buttonKey: string): void {
    if (this.buttonLoadingMap.has(buttonKey)) {
      this.buttonLoadingMap.get(buttonKey)!.next(false);
    }
  }

  /**
   * Get observable for button loading state
   * @param buttonKey Unique identifier for the button
   * @returns Observable that emits true when loading, false otherwise
   */
  isButtonLoading$(buttonKey: string): Observable<boolean> {
    if (!this.buttonLoadingMap.has(buttonKey)) {
      this.buttonLoadingMap.set(buttonKey, new BehaviorSubject<boolean>(false));
    }
    return this.buttonLoadingMap.get(buttonKey)!.asObservable();
  }

  /**
   * Check if a button is currently loading (synchronous)
   * @param buttonKey Unique identifier for the button
   * @returns true if button is loading, false otherwise
   */
  isButtonLoading(buttonKey: string): boolean {
    return this.buttonLoadingMap.has(buttonKey) && this.buttonLoadingMap.get(buttonKey)!.value;
  }

  /**
   * Hide all button spinners
   */
  hideAllButtonSpinners(): void {
    this.buttonLoadingMap.forEach(subject => subject.next(false));
  }
}
